import React, { useEffect, useState } from 'react';
import { footerApi, menuApi } from '../../../services/api';
import type { FooterSettings } from '../types';
import { Plus, Trash2, Pencil, Check, X, LayoutTemplate, ArrowUp, ArrowDown } from 'lucide-react';

const FooterEditor: React.FC = () => {
  type SocialLink = { platform: string; url: string };
  type ResourceLink = { title: string; url: string };
  type LegalLink = { title: string; url: string };

  const normalizeSettings = (data: unknown): FooterSettings => {
    const next = { ...((data && typeof data === 'object') ? data as Record<string, unknown> : {}) } as FooterSettings;
    const parseJsonArray = (value: unknown) => {
      if (Array.isArray(value)) return value;
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
      return [];
    };
    next.social_links = parseJsonArray(next.social_links);
    next.resources_links = parseJsonArray(next.resources_links);
    next.legal_links = parseJsonArray(next.legal_links);
    return next;
  };

  const [settings, setSettings] = useState<FooterSettings>({});
  const [menuItems, setMenuItems] = useState<{ id?: number; title: string; url: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [newMenuItem, setNewMenuItem] = useState({ title: '', url: '' });
  const [newSocial, setNewSocial] = useState({ platform: '', url: '' });
  const [newResource, setNewResource] = useState({ title: '', url: '' });
  const [newLegal, setNewLegal] = useState({ title: '', url: '' });
  const [editingMenuItem, setEditingMenuItem] = useState<{ id: number; title: string; url: string } | null>(null);
  const [saveMessage, setSaveMessage] = useState<string>('');
  const [saveError, setSaveError] = useState<string>('');

  useEffect(() => {
    const load = async () => {
      try {
        const r = await footerApi.getSettings();
        if (r?.success) setSettings(normalizeSettings(r.data || {}));
      } catch (e) {
        console.error(e);
      }
      try {
        const m = await menuApi.list('footer');
        if (m?.success && Array.isArray(m.data)) {
          type MenuNode = { id: number; title?: string; url?: string; children?: MenuNode[] };
          const flatten = (nodes: MenuNode[], acc: { id: number; title: string; url: string }[] = []) => {
            for (const n of nodes) {
              acc.push({ id: n.id, title: String(n.title || ''), url: String(n.url || '/') });
              if (Array.isArray(n.children)) flatten(n.children, acc);
            }
            return acc;
          };
          setMenuItems(flatten(m.data));
        }
      } catch (e) {
        console.error(e);
      }
    };
    load();
  }, []);

  const setField = (k: keyof FooterSettings, v: unknown) => {
    setSettings((prev) => ({ ...prev, [k]: v }));
  };

  const save = async () => {
    setSaving(true);
    setSaveMessage('');
    setSaveError('');
    try {
      const payload: Record<string, unknown> = { ...settings };
      if (Array.isArray(payload.social_links)) payload.social_links = JSON.stringify(payload.social_links);
      if (Array.isArray(payload.resources_links)) payload.resources_links = JSON.stringify(payload.resources_links);
      if (Array.isArray(payload.legal_links)) payload.legal_links = JSON.stringify(payload.legal_links);
      const r = await footerApi.updateSettings(payload);
      if (r?.success) {
        const normalized = normalizeSettings(r.data || payload);
        setSettings(normalized);
        try { localStorage.setItem('footerSettings', JSON.stringify(normalized)); } catch { void 0; }
        try {
          const fresh = await footerApi.getSettings();
          if (fresh?.success) {
            const freshNormalized = normalizeSettings(fresh.data || {});
            setSettings(freshNormalized);
            try { localStorage.setItem('footerSettings', JSON.stringify(freshNormalized)); } catch { void 0; }
          }
        } catch { void 0; }
        window.dispatchEvent(new Event('footer-settings-updated'));
        setSaveMessage('Footer settings saved successfully.');
      } else {
        setSaveError(r?.message || 'Failed to save footer settings.');
      }
    } catch (e) {
      console.error(e);
      setSaveError(e instanceof Error ? e.message : 'Failed to save footer settings.');
    }
    setSaving(false);
  };

  const reloadMenu = async () => {
    try {
      const m = await menuApi.list('footer');
      if (m?.success && Array.isArray(m.data)) {
        type MenuNode = { id: number; title?: string; url?: string; children?: MenuNode[] };
        const flatten = (nodes: MenuNode[], acc: { id: number; title: string; url: string }[] = []) => {
          for (const n of nodes) {
            acc.push({ id: n.id, title: String(n.title || ''), url: String(n.url || '/') });
            if (Array.isArray(n.children)) flatten(n.children, acc);
          }
          return acc;
        };
        setMenuItems(flatten(m.data));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const addMenuItem = async () => {
    const t = newMenuItem.title.trim();
    const u = newMenuItem.url.trim();
    if (!t || !u) return;
    try {
      await menuApi.create({ title: t, url: u, is_active: true, menu_type: 'footer' });
      setNewMenuItem({ title: '', url: '' });
      await reloadMenu();
    } catch (e) {
      console.error(e);
    }
  };

  const updateMenuItem = async () => {
    if (!editingMenuItem) return;
    try {
      await menuApi.update(editingMenuItem.id, { title: editingMenuItem.title, url: editingMenuItem.url });
      setEditingMenuItem(null);
      await reloadMenu();
    } catch (e) {
      console.error(e);
    }
  };

  const deleteMenuItem = async (id?: number) => {
    if (!id) return;
    try {
      await menuApi.delete(id);
      await reloadMenu();
    } catch (e) {
      console.error(e);
    }
  };

  const moveMenuItem = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= menuItems.length) return;
    const next = [...menuItems];
    [next[index], next[target]] = [next[target], next[index]];
    setMenuItems(next);
    try {
      const updates = next
        .filter((item): item is { id: number; title: string; url: string } => typeof item.id === 'number')
        .map((item, order) => ({ id: item.id, order }));
      if (updates.length > 0) {
        await menuApi.reorder(updates);
      }
    } catch (e) {
      console.error(e);
      await reloadMenu();
    }
  };

  const moveArrayItem = (field: 'social_links' | 'resources_links' | 'legal_links', index: number, direction: -1 | 1) => {
    const target = index + direction;
    const arr = Array.isArray(settings[field]) ? [...(settings[field] as unknown[])] : [];
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    setField(field, arr);
  };

  const updateSocialItem = (index: number, key: keyof SocialLink, value: string) => {
    const arr = Array.isArray(settings.social_links) ? [...settings.social_links] : [];
    if (!arr[index]) return;
    arr[index] = { ...arr[index], [key]: value };
    setField('social_links', arr);
  };

  const updateResourceItem = (index: number, key: keyof ResourceLink, value: string) => {
    const arr = Array.isArray(settings.resources_links) ? [...settings.resources_links] : [];
    if (!arr[index]) return;
    arr[index] = { ...arr[index], [key]: value };
    setField('resources_links', arr);
  };

  const updateLegalItem = (index: number, key: keyof LegalLink, value: string) => {
    const arr = Array.isArray(settings.legal_links) ? [...settings.legal_links] : [];
    if (!arr[index]) return;
    arr[index] = { ...arr[index], [key]: value };
    setField('legal_links', arr);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6 pb-24">
      <div className="flex items-center justify-between mb-6 sticky top-0 bg-[#0b0c15] z-10 py-4 border-b border-white/10">
        <div className="flex items-center gap-3 text-white">
          <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
            <LayoutTemplate size={20} className="text-cyan-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Footer Content Editor</h1>
            <p className="text-xs text-gray-400">Color settings removed. Manage only content/text.</p>
          </div>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="px-6 py-2 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-medium hover:from-cyan-500 hover:to-blue-500 transition-all"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {saveMessage && <div className="text-sm text-green-400 bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-2">{saveMessage}</div>}
      {saveError && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{saveError}</div>}

      <section className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-4">
        <h2 className="text-lg font-semibold text-white">Main Footer Text</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-gray-400">Top Line Text (The Future is Now)</label>
            <input
              value={settings.cta_eyebrow_text || ''}
              onChange={(e) => setField('cta_eyebrow_text', e.target.value)}
              className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white mt-1"
              placeholder="The Future is Now"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400">Prefix Text (Ready with)</label>
            <input
              value={settings.hero_prefix_text || ''}
              onChange={(e) => setField('hero_prefix_text', e.target.value)}
              className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white mt-1"
              placeholder="Ready with"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400">Brand Text</label>
            <input
              value={settings.watermark_text || ''}
              onChange={(e) => setField('watermark_text', e.target.value)}
              className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white mt-1"
              placeholder="EMPRISE"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400">Description Under Title</label>
            <textarea
              value={settings.button_description || ''}
              onChange={(e) => setField('button_description', e.target.value)}
              className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white mt-1 h-20 resize-none"
              placeholder="Bridge the gap between vision and reality..."
            />
          </div>
        </div>
      </section>

      <section className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-4">
        <h2 className="text-lg font-semibold text-white">CTA Button</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-gray-400">Button Text</label>
            <input
              value={settings.button_text || ''}
              onChange={(e) => setField('button_text', e.target.value)}
              className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white mt-1"
              placeholder="Start Your Journey"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400">Button Action</label>
            <select
              value={settings.button_action || 'modal'}
              onChange={(e) => setField('button_action', e.target.value as 'modal' | 'link')}
              className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white mt-1"
            >
              <option value="modal">Open Contact Form</option>
              <option value="link">Open Custom Link</option>
            </select>
          </div>
          {settings.button_action === 'link' && (
            <div className="md:col-span-2">
              <label className="text-xs text-gray-400">Button Link URL</label>
              <input
                value={settings.button_link || ''}
                onChange={(e) => setField('button_link', e.target.value)}
                className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white mt-1"
                placeholder="https://example.com"
              />
            </div>
          )}
        </div>
      </section>

      <section className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-4">
        <h2 className="text-lg font-semibold text-white">Section Headings & Contact</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="text-xs text-gray-400">Navigation Heading</label>
            <input value={settings.navigation_heading_text || ''} onChange={(e) => setField('navigation_heading_text', e.target.value)} className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white mt-1" placeholder="Navigation" />
          </div>
          <div>
            <label className="text-xs text-gray-400">Resources Heading</label>
            <input value={settings.resources_heading_text || ''} onChange={(e) => setField('resources_heading_text', e.target.value)} className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white mt-1" placeholder="Resources" />
          </div>
          <div>
            <label className="text-xs text-gray-400">Connect Heading</label>
            <input value={settings.connect_heading_text || ''} onChange={(e) => setField('connect_heading_text', e.target.value)} className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white mt-1" placeholder="Connect" />
          </div>
          <div>
            <label className="text-xs text-gray-400">Contact Email</label>
            <input value={settings.contact_email || ''} onChange={(e) => setField('contact_email', e.target.value)} className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white mt-1" placeholder="hq@emprise.ai" />
          </div>
          <div>
            <label className="text-xs text-gray-400">Contact Phone</label>
            <input value={settings.contact_phone || ''} onChange={(e) => setField('contact_phone', e.target.value)} className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white mt-1" placeholder="+1 000 000 0000" />
          </div>
          <div className="md:col-span-2 lg:col-span-3">
            <label className="text-xs text-gray-400">Company Description</label>
            <textarea value={settings.company_description || ''} onChange={(e) => setField('company_description', e.target.value)} className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white mt-1 h-20 resize-none" placeholder="Global leaders in..." />
          </div>
        </div>
      </section>

      <section className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-4">
        <h2 className="text-lg font-semibold text-white">Footer Navigation Links</h2>
        <div className="space-y-2">
          {menuItems.map((item, i) => (
            <div key={item.id} className="flex items-center gap-2 bg-black/20 border border-white/10 rounded-lg p-2">
              {editingMenuItem?.id === item.id ? (
                <>
                  <input value={editingMenuItem.title} onChange={(e) => setEditingMenuItem({ ...editingMenuItem, title: e.target.value })} className="flex-1 bg-black/30 border border-white/10 rounded px-2 py-1 text-white text-sm" />
                  <input value={editingMenuItem.url} onChange={(e) => setEditingMenuItem({ ...editingMenuItem, url: e.target.value })} className="flex-1 bg-black/30 border border-white/10 rounded px-2 py-1 text-white text-sm" />
                  <button onClick={updateMenuItem} className="text-green-400 p-1"><Check size={14} /></button>
                  <button onClick={() => setEditingMenuItem(null)} className="text-gray-400 p-1"><X size={14} /></button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-sm text-white">{item.title}</span>
                  <span className="flex-1 text-xs text-gray-400 truncate">{item.url}</span>
                  <button
                    onClick={() => moveMenuItem(i, -1)}
                    disabled={i === 0}
                    className={`p-1 ${i === 0 ? 'text-gray-600 cursor-not-allowed' : 'text-gray-300 hover:text-white'}`}
                    title="Move Up"
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    onClick={() => moveMenuItem(i, 1)}
                    disabled={i === menuItems.length - 1}
                    className={`p-1 ${i === menuItems.length - 1 ? 'text-gray-600 cursor-not-allowed' : 'text-gray-300 hover:text-white'}`}
                    title="Move Down"
                  >
                    <ArrowDown size={14} />
                  </button>
                  <button onClick={() => setEditingMenuItem({ id: item.id!, title: item.title, url: item.url })} className="text-blue-400 p-1"><Pencil size={14} /></button>
                  <button onClick={() => deleteMenuItem(item.id)} className="text-red-400 p-1"><Trash2 size={14} /></button>
                </>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={newMenuItem.title} onChange={(e) => setNewMenuItem({ ...newMenuItem, title: e.target.value })} className="flex-1 bg-black/20 border border-white/10 rounded px-3 py-2 text-white text-sm" placeholder="Link title" />
          <input value={newMenuItem.url} onChange={(e) => setNewMenuItem({ ...newMenuItem, url: e.target.value })} className="flex-1 bg-black/20 border border-white/10 rounded px-3 py-2 text-white text-sm" placeholder="/about" />
          <button onClick={addMenuItem} className="px-3 bg-white/10 rounded text-white hover:bg-white/20"><Plus size={16} /></button>
        </div>
      </section>

      <section className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-4">
        <h2 className="text-lg font-semibold text-white">Social Links</h2>
        <div className="space-y-2">
          {(Array.isArray(settings.social_links) ? settings.social_links : []).map((s: SocialLink, i: number) => (
            <div key={i} className="flex items-center gap-2 bg-black/20 border border-white/10 rounded-lg p-2">
              <input
                value={s.platform || ''}
                onChange={(e) => updateSocialItem(i, 'platform', e.target.value)}
                className="flex-1 bg-black/30 border border-white/10 rounded px-2 py-1 text-white text-sm"
                placeholder="Platform"
              />
              <input
                value={s.url || ''}
                onChange={(e) => updateSocialItem(i, 'url', e.target.value)}
                className="flex-1 bg-black/30 border border-white/10 rounded px-2 py-1 text-white text-sm"
                placeholder="https://..."
              />
              <button
                onClick={() => moveArrayItem('social_links', i, -1)}
                disabled={i === 0}
                className={`p-1 ${i === 0 ? 'text-gray-600 cursor-not-allowed' : 'text-gray-300 hover:text-white'}`}
                title="Move Up"
              >
                <ArrowUp size={14} />
              </button>
              <button
                onClick={() => moveArrayItem('social_links', i, 1)}
                disabled={i === (Array.isArray(settings.social_links) ? settings.social_links.length - 1 : 0)}
                className={`p-1 ${i === (Array.isArray(settings.social_links) ? settings.social_links.length - 1 : 0) ? 'text-gray-600 cursor-not-allowed' : 'text-gray-300 hover:text-white'}`}
                title="Move Down"
              >
                <ArrowDown size={14} />
              </button>
              <button
                onClick={() => {
                  const arr = Array.isArray(settings.social_links) ? [...settings.social_links] : [];
                  arr.splice(i, 1);
                  setField('social_links', arr);
                }}
                className="text-red-400 p-1"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={newSocial.platform} onChange={(e) => setNewSocial({ ...newSocial, platform: e.target.value })} className="flex-1 bg-black/20 border border-white/10 rounded px-3 py-2 text-white text-sm" placeholder="Platform (Instagram)" />
          <input value={newSocial.url} onChange={(e) => setNewSocial({ ...newSocial, url: e.target.value })} className="flex-1 bg-black/20 border border-white/10 rounded px-3 py-2 text-white text-sm" placeholder="https://..." />
          <button
            onClick={() => {
              const p = newSocial.platform.trim();
              const u = newSocial.url.trim();
              if (!p || !u) return;
              const arr = Array.isArray(settings.social_links) ? [...settings.social_links] : [];
              arr.push({ platform: p, url: u });
              setField('social_links', arr);
              setNewSocial({ platform: '', url: '' });
            }}
            className="px-3 bg-white/10 rounded text-white hover:bg-white/20"
          >
            <Plus size={16} />
          </button>
        </div>
      </section>

      <section className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-4">
        <h2 className="text-lg font-semibold text-white">Resources Links</h2>
        <div className="space-y-2">
          {(Array.isArray(settings.resources_links) ? settings.resources_links : []).map((r: ResourceLink, i: number) => (
            <div key={i} className="flex items-center gap-2 bg-black/20 border border-white/10 rounded-lg p-2">
              <input
                value={r.title || ''}
                onChange={(e) => updateResourceItem(i, 'title', e.target.value)}
                className="flex-1 bg-black/30 border border-white/10 rounded px-2 py-1 text-white text-sm"
                placeholder="Resource Title"
              />
              <input
                value={r.url || ''}
                onChange={(e) => updateResourceItem(i, 'url', e.target.value)}
                className="flex-1 bg-black/30 border border-white/10 rounded px-2 py-1 text-white text-sm"
                placeholder="https://..."
              />
              <button
                onClick={() => moveArrayItem('resources_links', i, -1)}
                disabled={i === 0}
                className={`p-1 ${i === 0 ? 'text-gray-600 cursor-not-allowed' : 'text-gray-300 hover:text-white'}`}
                title="Move Up"
              >
                <ArrowUp size={14} />
              </button>
              <button
                onClick={() => moveArrayItem('resources_links', i, 1)}
                disabled={i === (Array.isArray(settings.resources_links) ? settings.resources_links.length - 1 : 0)}
                className={`p-1 ${i === (Array.isArray(settings.resources_links) ? settings.resources_links.length - 1 : 0) ? 'text-gray-600 cursor-not-allowed' : 'text-gray-300 hover:text-white'}`}
                title="Move Down"
              >
                <ArrowDown size={14} />
              </button>
              <button
                onClick={() => {
                  const arr = Array.isArray(settings.resources_links) ? [...settings.resources_links] : [];
                  arr.splice(i, 1);
                  setField('resources_links', arr);
                }}
                className="text-red-400 p-1"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={newResource.title} onChange={(e) => setNewResource({ ...newResource, title: e.target.value })} className="flex-1 bg-black/20 border border-white/10 rounded px-3 py-2 text-white text-sm" placeholder="Case Studies" />
          <input value={newResource.url} onChange={(e) => setNewResource({ ...newResource, url: e.target.value })} className="flex-1 bg-black/20 border border-white/10 rounded px-3 py-2 text-white text-sm" placeholder="/case-studies" />
          <button
            onClick={() => {
              const t = newResource.title.trim();
              const u = newResource.url.trim();
              if (!t || !u) return;
              const arr = Array.isArray(settings.resources_links) ? [...settings.resources_links] : [];
              arr.push({ title: t, url: u });
              setField('resources_links', arr);
              setNewResource({ title: '', url: '' });
            }}
            className="px-3 bg-white/10 rounded text-white hover:bg-white/20"
          >
            <Plus size={16} />
          </button>
        </div>
      </section>

      <section className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-4">
        <h2 className="text-lg font-semibold text-white">Legal & Bottom Text</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="text-xs text-gray-400">Copyright Text</label>
            <input value={settings.copyright_text || ''} onChange={(e) => setField('copyright_text', e.target.value)} className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white mt-1" placeholder="© 2026 EMPRISE..." />
          </div>
          <div>
            <label className="text-xs text-gray-400">Newsletter Title</label>
            <input value={settings.newsletter_title || ''} onChange={(e) => setField('newsletter_title', e.target.value)} className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white mt-1" placeholder="Join Link" />
          </div>
          <div>
            <label className="text-xs text-gray-400">Newsletter Subtitle</label>
            <input value={settings.newsletter_subtitle || ''} onChange={(e) => setField('newsletter_subtitle', e.target.value)} className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white mt-1" placeholder="Subscribe..." />
          </div>
        </div>
        <div className="space-y-2">
          {(Array.isArray(settings.legal_links) ? settings.legal_links : []).map((l: LegalLink, i: number) => (
            <div key={i} className="flex items-center gap-2 bg-black/20 border border-white/10 rounded-lg p-2">
              <input
                value={l.title || ''}
                onChange={(e) => updateLegalItem(i, 'title', e.target.value)}
                className="flex-1 bg-black/30 border border-white/10 rounded px-2 py-1 text-white text-sm"
                placeholder="Privacy Policy"
              />
              <input
                value={l.url || ''}
                onChange={(e) => updateLegalItem(i, 'url', e.target.value)}
                className="flex-1 bg-black/30 border border-white/10 rounded px-2 py-1 text-white text-sm"
                placeholder="/privacy"
              />
              <button
                onClick={() => moveArrayItem('legal_links', i, -1)}
                disabled={i === 0}
                className={`p-1 ${i === 0 ? 'text-gray-600 cursor-not-allowed' : 'text-gray-300 hover:text-white'}`}
                title="Move Up"
              >
                <ArrowUp size={14} />
              </button>
              <button
                onClick={() => moveArrayItem('legal_links', i, 1)}
                disabled={i === (Array.isArray(settings.legal_links) ? settings.legal_links.length - 1 : 0)}
                className={`p-1 ${i === (Array.isArray(settings.legal_links) ? settings.legal_links.length - 1 : 0) ? 'text-gray-600 cursor-not-allowed' : 'text-gray-300 hover:text-white'}`}
                title="Move Down"
              >
                <ArrowDown size={14} />
              </button>
              <button
                onClick={() => {
                  const arr = Array.isArray(settings.legal_links) ? [...settings.legal_links] : [];
                  arr.splice(i, 1);
                  setField('legal_links', arr);
                }}
                className="text-red-400 p-1"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={newLegal.title} onChange={(e) => setNewLegal({ ...newLegal, title: e.target.value })} className="flex-1 bg-black/20 border border-white/10 rounded px-3 py-2 text-white text-sm" placeholder="Privacy Policy" />
          <input value={newLegal.url} onChange={(e) => setNewLegal({ ...newLegal, url: e.target.value })} className="flex-1 bg-black/20 border border-white/10 rounded px-3 py-2 text-white text-sm" placeholder="/privacy" />
          <button
            onClick={() => {
              const t = newLegal.title.trim();
              const u = newLegal.url.trim();
              if (!t || !u) return;
              const arr = Array.isArray(settings.legal_links) ? [...settings.legal_links] : [];
              arr.push({ title: t, url: u });
              setField('legal_links', arr);
              setNewLegal({ title: '', url: '' });
            }}
            className="px-3 bg-white/10 rounded text-white hover:bg-white/20"
          >
            <Plus size={16} />
          </button>
        </div>
      </section>
    </div>
  );
};

export default FooterEditor;
